import { hide, jsx, type JsxChild, type JsxElementProps, show } from '@amazon/vinyl-tsx'
import { type ObservableValue } from '@amazon/vinyl-observable'
import { Icon } from '@/components/Icon'
import { getString } from '@/components/i18n/bundle.generated'
import { modalCount } from '@/style/modalVisible'
import { ctx } from '@/util/context'
import { createDisposer, logDebug } from '@amazon/vinyl-util'
import { onNavigate } from '@/router/router'
import { onExternalLinkPress } from '@/components/linkPress'
import { modalOpeningFocus, trapDialogFocus } from '@/components/dialogFocus'
import { type MetricsViewedEntity, reportClick, reportContentView } from '@/metrics/uiMetrics'
import { mediaQuery$ } from '@/util/mediaQuery'
import { bindSheetSwipe } from '@/components/sheetSwipe'

export const SHEET_MEDIA_QUERY = '(max-width: 479px)'

export type CloseReason = 'backdrop' | 'keypress' | 'button' | 'swipe' | 'navigate'

export interface ModalProps extends JsxElementProps<'div'> {
    readonly visible: ObservableValue<boolean>

    /**
     * Invoked when the user requests the modal to be closed, either via Escape, clicking the background, or
     * clicking the close button. At 479px wide or less the modal is a bottom sheet: no close button, the
     * handle and a swipe down close it instead.
     */
    readonly onClose: (reason: CloseReason) => void

    /**
     * Pins the content region to this percent of the viewport height instead
     * of hugging its content (e.g. server-driven Dynamic Messaging sizing).
     * Only values in `(0, 100]` apply; anything else keeps the default hug.
     */
    readonly fillHeightPercent?: number

    /** False drops the close button for a modal only its own actions can close. */
    readonly dismissible?: boolean

    /**
     * GH's modal `contentName`: each opening sends a uiContentView with it, and the close button and
     * backdrop send uiClick `selectDismiss` with it before `onClose`. Escape and navigation send none.
     */
    readonly contentName?: string

    /** The entities GH's content view of this modal names (the playlist being edited). */
    readonly entities?: readonly MetricsViewedEntity[]

    /** True when the caller sends its own content view (with entities, or once loaded). */
    readonly ownContentView?: boolean

    /** False when the caller sends its own dismiss click. */
    readonly dismissMetric?: boolean
}

export function Modal(
    {
        onClose,
        className,
        visible,
        fillHeightPercent,
        dismissible = true,
        contentName,
        entities,
        ownContentView = false,
        dismissMetric = true,
        onConnect: onConnectProp,
        ...rest
    }: ModalProps,
    children: JsxChild[]
) {
    const { target } = ctx('Modal')
    const fillHeight =
        fillHeightPercent !== undefined &&
        Number.isFinite(fillHeightPercent) &&
        fillHeightPercent > 0
            ? Math.min(fillHeightPercent, 100)
            : undefined

    const closeByGesture = (reason: 'backdrop' | 'button' | 'swipe') => {
        if (contentName && dismissible && dismissMetric) {
            reportClick({ actionType: 'selectDismiss', contentName })
        }
        onClose(reason)
    }

    const sheet$ = mediaQuery$(SHEET_MEDIA_QUERY)

    return (
        <div
            className={`modal ${className ?? ''}`}
            role={dismissible ? 'dialog' : 'alertdialog'}
            aria-modal="true"
            tabIndex={-1}
            visible={false}
            {...rest}
            onConnect={(view) => {
                const { add, dispose } = createDisposer()

                // A caller's own `onConnect` would otherwise be silently
                // dropped: it arrives via `rest` and, spread before this
                // literal `onConnect`, loses to it under plain object-key
                // overwrite. Run it (and track its own cleanup) alongside
                // this component's own connect wiring instead.
                if (onConnectProp) {
                    const cleanup = onConnectProp(view)
                    if (cleanup) add(cleanup)
                }

                let releaseFocus: (() => void) | null = null
                const release = () => {
                    releaseFocus?.()
                    releaseFocus = null
                }

                let showing = false
                add(
                    visible.onData((value) => {
                        if (showing === value) return // no-op
                        logDebug(target, 'modal visibility:', value)
                        showing = value
                        // Setting the modal count will apply the modalVisible class to the root scrolling element
                        // when a modal is showing.
                        if (value) modalCount.value++
                        else modalCount.value--
                        if (value) {
                            if (contentName && !ownContentView)
                                reportContentView({
                                    contentName,
                                    ...(entities ? { entities } : null),
                                })
                            show(view)
                            releaseFocus = trapDialogFocus(view, {
                                close: () => onClose('keypress'),
                                initialFocus: modalOpeningFocus(view),
                            })
                        } else {
                            hide(view)
                            release()
                        }
                    })
                )

                add(() => {
                    if (showing) modalCount.value--
                    release()
                })

                add(
                    onNavigate(() => {
                        onClose('navigate')
                    })
                )
                add(onExternalLinkPress(() => onClose('navigate')))

                return dispose
            }}
        >
            <div className="backdrop" onclick={() => closeByGesture('backdrop')} />
            <div
                className="content"
                onConnect={(content) => {
                    if (fillHeight !== undefined)
                        content.style.setProperty('--modalFillHeight', `${fillHeight}vh`)
                }}
            >
                <div
                    className="dismissControl"
                    onConnect={(slot) => {
                        const { add, dispose } = createDisposer()
                        const content = slot.parentElement as HTMLElement
                        add(
                            sheet$.onData((sheet) => {
                                if (!dismissible) return slot.replaceChildren()
                                slot.replaceChildren(
                                    sheet ? (
                                        <button
                                            className="button sheetHandle"
                                            data-dialog-dismiss=""
                                            aria-label={getString(
                                                'dmusic_modal_gesture_handle_accessibility_label'
                                            )}
                                            title={getString(
                                                'dmusic_modal_gesture_handle_accessibility_hint'
                                            )}
                                            onclick={() => closeByGesture('button')}
                                        >
                                            <span className="sheetHandleBar" />
                                        </button>
                                    ) : (
                                        <button
                                            className="button closeBtn"
                                            data-dialog-dismiss=""
                                            aria-label={getString(
                                                'dmusic_modal_close_button_accessibility_label'
                                            )}
                                            title={getString(
                                                'dmusic_modal_close_button_accessibility_label'
                                            )}
                                            onclick={() => closeByGesture('button')}
                                        >
                                            <Icon icon="cancel" />
                                        </button>
                                    )
                                )
                            })
                        )
                        if (dismissible) {
                            add(
                                bindSheetSwipe(content, {
                                    enabled: () => sheet$.value,
                                    onDismiss: () => closeByGesture('swipe'),
                                })
                            )
                        }
                        return dispose
                    }}
                />
                {...children}
            </div>
        </div>
    )
}
